/**
 * Everything the backend knows about a supplement plan: listing a client's,
 * reading one whole, writing one, sending it, and rendering it as a document.
 *
 * All of it bar deleting, which has no endpoint — a plan made here cannot be
 * removed.
 *
 * Everything snake_case stops at `supplementWire`, which this is the only
 * caller of — the rest of the module sees `SupplementPlanSummary` and
 * `PlanSupplement`. The conversions sit there rather than here so the suites
 * can run their fixtures through them while this module is mocked.
 */

import { api, API_ENDPOINTS } from "../../../lib/api";
import type { PaginatedResponse } from "../../../lib/api";
import {
  DEFAULT_PAGE_SIZE,
  MAX_PAGE_SIZE,
  toSupplementPayloads,
  toSupplementPlanRecord,
  toSupplementPlanStatusParam,
  toSupplementPlanSummary,
} from "../supplementWire";
import type { GetToken } from "../../auth/types/auth.types";
import type {
  PlanSupplement,
  SaveSupplementPlanPayload,
  SendSupplementPlanPayload,
  SendSupplementPlanResponse,
  SupplementPlanDetailResponse,
  SupplementPlanRecord,
  SupplementPlanState,
  SupplementPlanSummaryPage,
  SupplementPlanSummaryResponse,
} from "../types/supplements.types";

/**
 * How long the document call is given. A minute, matching the meal plan's:
 * rendering is work, and the client's default is written for calls that read
 * a row back.
 */
const PDF_TIMEOUT_MS = 60_000;

// Re-exported so a call site reads one module rather than two — the page size
// is about this endpoint, whichever file the number is written in.
export { DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE };

export interface FetchSupplementPlansOptions {
  /** A client's `public_id`. Absent asks for the whole workspace. */
  client?: string;
  /**
   * `"all"` leaves the filter off entirely, which is how both are asked for.
   * Otherwise `status=DRAFT` or `status=ACTIVE`, the same pair the meal plan
   * list takes — see `toSupplementPlanStatusParam`.
   */
  status?: SupplementPlanState | "all";
  /**
   * Narrows by plan name, on the server — so the box narrows the whole list
   * rather than the page in hand. The exercise list has no such parameter,
   * which is why its own search works the other way.
   */
  search?: string;
  /** 1-based, the way the endpoint counts. */
  page?: number;
  pageSize?: number;
  signal?: AbortSignal;
}

/** One page of a client's supplement plans. */
export async function fetchSupplementPlans(
  getToken: GetToken,
  {
    client,
    status = "all",
    search,
    page,
    pageSize = DEFAULT_PAGE_SIZE,
    signal,
  }: FetchSupplementPlansOptions = {}
): Promise<SupplementPlanSummaryPage> {
  const token = await getToken();
  if (!token) {
    // Not an ApiError: the server was never asked. Calling this without an
    // active session is a bug at the call site, not something to show a user.
    throw new Error("No active Clerk session - cannot load supplement plans.");
  }

  const response = await api.get<
    PaginatedResponse<SupplementPlanSummaryResponse>
  >(API_ENDPOINTS.supplementPlans.list, {
    token,
    // `undefined` entries are dropped by the API client, so an unfiltered ask
    // never puts an empty `client=` or `search=` in the URL.
    query: {
      client: client || undefined,
      status: toSupplementPlanStatusParam(status),
      search: search?.trim() || undefined,
      page,
      page_size: Math.min(pageSize, MAX_PAGE_SIZE),
    },
    signal,
  });

  return {
    count: response?.count ?? 0,
    hasNext: !!response?.next,
    plans: (response?.results ?? []).map(toSupplementPlanSummary),
  };
}

/**
 * One plan, by `public_id` — the only place the supplements on a plan come
 * from, which is what the create screen opens on.
 */
export async function fetchSupplementPlan(
  getToken: GetToken,
  publicId: string,
  signal?: AbortSignal
): Promise<SupplementPlanRecord> {
  const token = await getToken();
  if (!token) {
    throw new Error(
      "No active Clerk session - cannot load the supplement plan."
    );
  }

  const detail = await api.get<SupplementPlanDetailResponse>(
    API_ENDPOINTS.supplementPlans.detail(publicId),
    { token, signal }
  );

  return toSupplementPlanRecord(detail);
}

export interface SaveSupplementPlanInput {
  /** The client's `public_id`. Sent on the update as well as the create. */
  clientId: string;
  /** The cards as the screen holds them — converted on the way out. */
  supplements: PlanSupplement[];
}

/**
 * Writes a new plan, and hands back what the backend made of it.
 *
 * The response is the whole plan — its `public_id`, the name and number the
 * backend gave it ("Sup/W01/00004"), and the supplements as stored with their
 * units and meals resolved to names — so a create is also a read, and nothing
 * has to be fetched again to show what was just written.
 */
export async function createSupplementPlan(
  getToken: GetToken,
  { clientId, supplements }: SaveSupplementPlanInput
): Promise<SupplementPlanRecord> {
  const token = await getToken();
  if (!token) {
    throw new Error(
      "No active Clerk session - cannot create a supplement plan."
    );
  }

  const body: SaveSupplementPlanPayload = {
    client: clientId,
    supplements: toSupplementPayloads(supplements),
  };

  const detail = await api.post<SupplementPlanDetailResponse>(
    API_ENDPOINTS.supplementPlans.list,
    body,
    { token }
  );

  return toSupplementPlanRecord(detail);
}

/**
 * Rewrites one, and hands back what it now holds.
 *
 * A replace rather than a patch — the whole list goes up and the plan ends up
 * holding exactly that, the cards deleted from it included — which is what
 * makes the screen's Save mean what it looks like it means. The client travels
 * with it, the same way the create sends one.
 */
export async function updateSupplementPlan(
  getToken: GetToken,
  publicId: string,
  { clientId, supplements }: SaveSupplementPlanInput
): Promise<SupplementPlanRecord> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot save the supplement plan.");
  }

  const body: SaveSupplementPlanPayload = {
    client: clientId,
    supplements: toSupplementPayloads(supplements),
  };

  const detail = await api.put<SupplementPlanDetailResponse>(
    API_ENDPOINTS.supplementPlans.detail(publicId),
    body,
    { token }
  );

  return toSupplementPlanRecord(detail);
}

/**
 * Sends the plan to the addresses given, and hands back whatever the backend
 * had to say about it.
 *
 * The message is the backend's own wording, so a change there reaches the
 * dietitian without this module being touched. An answer with none is not a
 * failure — the call succeeding is what says the plan went.
 */
export async function sendSupplementPlan(
  getToken: GetToken,
  publicId: string,
  emails: string[]
): Promise<string | undefined> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot send the supplement plan.");
  }

  const body: SendSupplementPlanPayload = { emails };

  const response = await api.post<SendSupplementPlanResponse>(
    API_ENDPOINTS.supplementPlans.send(publicId),
    body,
    { token }
  );

  return response?.message?.trim() || undefined;
}

/**
 * The plan as a document, for the dietitian's own copy.
 *
 * The bytes rather than a link to them, so the caller hands the file straight
 * to the browser — see `saveSupplementPlanPdf`. The backend renders it because
 * it is the one that knows the dietitian's brand, and so that the document a
 * client is emailed and the one their dietitian downloads are the same
 * document.
 *
 * A longer timeout than the rest of the module: rendering a plan is work, and
 * the default is written for calls that only read a row back.
 */
export async function fetchSupplementPlanPdf(
  getToken: GetToken,
  publicId: string,
  signal?: AbortSignal
): Promise<Blob> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot download a plan.");
  }

  return api.getBlob(API_ENDPOINTS.supplementPlans.pdf(publicId), {
    token,
    signal,
    // A PDF for preference, and anything rather than nothing. `application/pdf`
    // alone is refused — see `mealPlans.pdf`, where the same wildcard is
    // explained at length.
    headers: { Accept: "application/pdf, */*" },
    timeoutMs: PDF_TIMEOUT_MS,
  });
}
